import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type ServiceName = 'openai' | 'perplexity' | 'together_ai' | 'serper' | 'heyreach';

export interface ClientApiKey {
  id: string;
  client_id: string;
  service_name: ServiceName;
  api_key: string;
  secret_key?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  notes?: string;
}

export interface NewClientApiKey {
  client_id: string;
  service_name: ServiceName;
  api_key: string;
  secret_key?: string;
  notes?: string;
  created_by?: string;
}

export function useClientApiKeys(clientId?: string) {
  const [apiKeys, setApiKeys] = useState<ClientApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchApiKeys = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from('client_api_keys')
        .select('*')
        .order('created_at', { ascending: false });

      if (clientId && clientId !== 'all') {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setApiKeys((data || []) as ClientApiKey[]);
    } catch (err: any) {
      console.error('Error fetching client API keys:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async (apiKeyData: NewClientApiKey) => {
    setLoading(true);
    setError(null);

    try {
      // First deactivate any existing active keys for this client/service
      const { error: deactivateError } = await supabase
        .from('client_api_keys')
        .update({ is_active: false })
        .eq('client_id', apiKeyData.client_id)
        .eq('service_name', apiKeyData.service_name)
        .eq('is_active', true);

      if (deactivateError) throw deactivateError;

      // Create new active key
      const { data, error } = await supabase
        .from('client_api_keys')
        .insert({
          ...apiKeyData,
          is_active: true
        })
        .select()
        .single();

      if (error) throw error;

      await fetchApiKeys();
      
      toast({
        title: "API Key Added",
        description: `${apiKeyData.service_name} API key has been added successfully.`,
      });

      return data;
    } catch (err: any) {
      console.error('Error creating API key:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: `Failed to add API key: ${err.message}`,
        variant: "destructive",
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateApiKey = async (id: string, updates: Partial<NewClientApiKey>) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('client_api_keys')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      await fetchApiKeys();
      
      toast({
        title: "API Key Updated",
        description: "API key has been updated successfully.",
      });

      return data;
    } catch (err: any) {
      console.error('Error updating API key:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: `Failed to update API key: ${err.message}`,
        variant: "destructive",
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteApiKey = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('client_api_keys')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await fetchApiKeys();
      
      toast({
        title: "API Key Deleted",
        description: "API key has been deleted successfully.",
      });
    } catch (err: any) {
      console.error('Error deleting API key:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: `Failed to delete API key: ${err.message}`,
        variant: "destructive",
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const activateApiKey = async (id: string, clientId: string, serviceName: string) => {
    setLoading(true);
    setError(null);

    try {
      // Deactivate all keys for this client/service
      const { error: deactivateError } = await supabase
        .from('client_api_keys')
        .update({ is_active: false })
        .eq('client_id', clientId)
        .eq('service_name', serviceName);

      if (deactivateError) throw deactivateError;

      // Activate the selected key
      const { error: activateError } = await supabase
        .from('client_api_keys')
        .update({ is_active: true })
        .eq('id', id);

      if (activateError) throw activateError;

      await fetchApiKeys();
      
      toast({
        title: "API Key Activated",
        description: "API key has been activated successfully.",
      });
    } catch (err: any) {
      console.error('Error activating API key:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: `Failed to activate API key: ${err.message}`,
        variant: "destructive",
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, [clientId]);

  return {
    apiKeys,
    loading,
    error,
    createApiKey,
    updateApiKey,
    deleteApiKey,
    activateApiKey,
    refetch: fetchApiKeys
  };
}